/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import type {
  GenerationRun,
  CreateGenerationRunRequest,
  CreateGenerationRunResponse,
  RetryGenerationRunResponse,
  GenerationRunStatus,
  InsightSynthesis,
  InsightStatusAction,
} from '../types/insights-synthesis';
import type { ApiResponse } from '../types/api';
import { g17ecoApi, transformResponse } from './g17ecoApi';

enum Tag {
  Runs = 'insights-synthesis-runs',
  RunDetails = 'insights-synthesis-run-details',
}

const getRunsUrl = (initiativeId: string, batchId?: string) =>
  `/initiatives/${initiativeId}/ai-supervisor/insights-runs${batchId ? `/${batchId}` : ''}`;

const getInsightStatusUrl = (initiativeId: string, insightId: string) =>
  `/initiatives/${initiativeId}/ai-supervisor/insights/${insightId}/status`;

export const insightsSynthesisApi = g17ecoApi
  .enhanceEndpoints({
    addTagTypes: Object.values(Tag),
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      listGenerationRuns: builder.query<
        GenerationRun[],
        { initiativeId: string; status?: GenerationRunStatus[] }
      >({
        transformResponse: (response: ApiResponse<{ runs: GenerationRun[] }>) => response.data.runs,
        query: ({ initiativeId, status }) => ({
          url: getRunsUrl(initiativeId),
          method: 'get',
          params: status?.length ? { status } : undefined,
        }),
        providesTags: (_result, _error, { initiativeId }) => [
          { type: Tag.Runs, id: initiativeId },
        ],
      }),

      createGenerationRun: builder.mutation<
        CreateGenerationRunResponse,
        { initiativeId: string; data: CreateGenerationRunRequest }
      >({
        transformResponse,
        query: ({ initiativeId, data }) => ({
          url: getRunsUrl(initiativeId),
          method: 'post',
          data,
        }),
        invalidatesTags: (_result, _error, { initiativeId }) => [
          { type: Tag.Runs, id: initiativeId },
        ],
      }),

      getRunWithInsights: builder.query<
        GenerationRun & { insights: InsightSynthesis[] },
        { initiativeId: string; batchId: string }
      >({
        transformResponse,
        query: ({ initiativeId, batchId }) => ({
          url: getRunsUrl(initiativeId, batchId),
          method: 'get',
        }),
        providesTags: (_result, _error, { batchId }) => [
          { type: Tag.RunDetails, id: batchId },
        ],
      }),

      updateInsightStatus: builder.mutation<
        InsightSynthesis,
        { initiativeId: string; insightId: string; status: InsightStatusAction }
      >({
        transformResponse,
        query: ({ initiativeId, insightId, status }) => ({
          url: getInsightStatusUrl(initiativeId, insightId),
          method: 'patch',
          data: { status },
        }),
        invalidatesTags: (_result, _error) => [{ type: Tag.RunDetails }],
      }),

      retryGenerationRun: builder.mutation<
        RetryGenerationRunResponse,
        { initiativeId: string; batchId: string }
      >({
        transformResponse,
        query: ({ initiativeId, batchId }) => ({
          url: `${getRunsUrl(initiativeId, batchId)}/retry`,
          method: 'post',
        }),
        invalidatesTags: (_result, _error, { batchId, initiativeId }) => [
          { type: Tag.RunDetails, id: batchId },
          { type: Tag.Runs, id: initiativeId },
        ],
      }),
    }),
  });

export const {
  useListGenerationRunsQuery,
  useCreateGenerationRunMutation,
  useGetRunWithInsightsQuery,
  useUpdateInsightStatusMutation,
  useRetryGenerationRunMutation,
} = insightsSynthesisApi;
